<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Select, Stack, Text } from '@Shelly/shelly-design-system';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import {
		deviceSupportsScripts,
		deviceHasScriptsFromAvailability,
		deviceHasNoScriptsFromAvailability,
		fetchUnknownRoomScriptsAvailability,
		getDeviceChannelFromKey,
		getDeviceRealId,
		loadDeviceScripts,
		mapScriptsToSelectOptions,
		resolveRoomScriptsAvailabilitySync,
		type RoomScriptsAvailability,
	} from 'src/helpers/device_script_action_helpers';
	import { is_run_script_action_ui } from 'src/types/RunScriptActionUI';
	import type { Device } from 'src/types/Device';
	import { devices, devices_settings, rooms } from 'src/stores';
	import { DO_WIZARD_STEPS_INDEXES } from '../../helpers/do-wizard-helpers';

	export let sceneData: any;
	export let firstSelectedRef: HTMLElement = null;

	const isEditingExistingRunScript = is_run_script_action_ui(sceneData.translated);
	let skipSelectionClearForEdit = isEditingExistingRunScript;
	let isLoadingRoomScripts = false;
	let fetchedScriptsAvailability: RoomScriptsAvailability = {};
	let roomScriptsPrefetchSeq = 0;
	let lastRoomPrefetchKey = '';
	const selectValue = (detail: unknown): string => {
		if (typeof detail === 'string' || typeof detail === 'number') {
			return String(detail);
		}
		if (detail && typeof detail === 'object' && 'value' in detail) {
			return String((detail as { value: unknown }).value ?? '');
		}
		return '';
	};

	const clearScriptListCache = () => {
		sceneData.scriptAction = {
			...sceneData.scriptAction,
			scriptListDeviceKey: '',
			scriptListRealId: '',
			scriptListOptions: [],
		};
	};

	if (isEditingExistingRunScript) {
		const deviceKey =
			Number(sceneData.translated.device.channel) > 0
				? `${sceneData.translated.device.id}_${sceneData.translated.device.channel}`
				: sceneData.translated.device.id;
		const device = $devices[deviceKey] as Device | undefined;
		sceneData.selectedDevice = {
			value: deviceKey,
			title: device?.name || deviceKey,
			id: deviceKey,
		};
		sceneData.scriptAction.device = { ...sceneData.translated.device };
		sceneData.scriptAction.scriptId = sceneData.translated.scriptId;
		sceneData.scriptAction.scriptName = sceneData.translated.scriptName;
		sceneData.scriptAction.command = sceneData.translated.command;
		sceneData.scriptAction.roomId = device ? String(device.room_id ?? '') : '';
		if (sceneData.translated.delay) {
			sceneData.scriptAction.delay = { ...sceneData.translated.delay };
		}

		if (device && sceneData.translated.scriptId != null) {
			fetchedScriptsAvailability = {
				...fetchedScriptsAvailability,
				[getDeviceRealId(device)]: true,
			};
		}
	}

	$: roomOptions = Object.entries($rooms || {}).map(([id, room]) => {
		const roomRecord = room as { name?: string };
		return {
			value: String(parseInt(id, 10)),
			title: roomRecord?.name || `Room ${id}`,
		};
	});

	$: selectedRoom = String(sceneData.scriptAction?.roomId ?? '');

	$: roomDeviceKeys = selectedRoom
		? Object.entries($devices)
				.filter(([, device]) => String((device as Device).room_id) === selectedRoom)
				.filter(([, device]) => deviceSupportsScripts(device as Device))
				.map(([deviceKey]) => deviceKey)
		: [];

	$: syncRoomScriptsAvailability =
		selectedRoom && roomDeviceKeys.length > 0
			? resolveRoomScriptsAvailabilitySync(
					roomDeviceKeys,
					$devices as Record<string, Device>,
					$devices_settings,
				).availability
			: {};

	$: deviceScriptsAvailability = {
		...syncRoomScriptsAvailability,
		...fetchedScriptsAvailability,
	};

	$: availableDevices = selectedRoom
		? roomDeviceKeys.map((deviceKey) => {
				const device = $devices[deviceKey] as Device;
				const isSelected = sceneData.selectedDevice?.value === deviceKey;
				const hasScripts = deviceHasScriptsFromAvailability(deviceScriptsAvailability, device);
				const hasNoScripts = deviceHasNoScriptsFromAvailability(
					deviceScriptsAvailability,
					device,
				);
				const isDisabled = !hasScripts && !isSelected;

				return {
					name: device?.name || deviceKey,
					id: deviceKey,
					value: deviceKey,
					selected: isSelected,
					disabled: isDisabled,
					descriptiveText: hasNoScripts
						? $format('no_scripts_on_device', {
								default: 'No scripts on this device',
							})
						: undefined,
				};
			})
		: [];

	$: selectedRoomOption = roomOptions.find((o) => String(o.value) === selectedRoom) ?? null;

	const clearInvalidDeviceSelection = () => {
		sceneData.selectedDevice = {};
		sceneData.scriptAction = {
			...sceneData.scriptAction,
			device: { id: undefined, channel: 0 },
			scriptId: undefined,
			scriptName: undefined,
		};
		clearScriptListCache();
		sceneData.isValid = false;
		sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.RUN_SCRIPT_DEVICE_STEP;
	};

	$: selectedDeviceForClear = $devices[sceneData.selectedDevice?.value] as Device | undefined;
	$: if (
		!isLoadingRoomScripts &&
		selectedRoom &&
		selectedDeviceForClear &&
		deviceHasNoScriptsFromAvailability(deviceScriptsAvailability, selectedDeviceForClear) &&
		!skipSelectionClearForEdit
	) {
		clearInvalidDeviceSelection();
	}

	const prefetchScriptsForRoom = async (roomId: string, deviceKeys: string[]) => {
		if (!roomId || deviceKeys.length === 0) {
			fetchedScriptsAvailability = {};
			isLoadingRoomScripts = false;
			return;
		}

		const prefetchSeq = ++roomScriptsPrefetchSeq;
		const devicesMap = $devices as Record<string, Device>;
		const syncResult = resolveRoomScriptsAvailabilitySync(
			deviceKeys,
			devicesMap,
			$devices_settings,
		);

		if (prefetchSeq !== roomScriptsPrefetchSeq) {
			return;
		}

		isLoadingRoomScripts = syncResult.unknownRealIds.length > 0;

		if (syncResult.unknownRealIds.length === 0) {
			isLoadingRoomScripts = false;
			return;
		}

		const fetched = await fetchUnknownRoomScriptsAvailability(
			syncResult.unknownRealIds,
			syncResult.deviceByRealId,
			$devices_settings,
			{
				abortSeq: prefetchSeq,
				getCurrentAbortSeq: () => roomScriptsPrefetchSeq,
			},
		);

		if (prefetchSeq !== roomScriptsPrefetchSeq) {
			return;
		}

		fetchedScriptsAvailability = { ...fetchedScriptsAvailability, ...fetched };
		isLoadingRoomScripts = false;
	};

	// Wait until room devices are listed — prefetching with an empty list would skip forever.
	$: if (selectedRoom && roomDeviceKeys.length > 0 && selectedRoom !== lastRoomPrefetchKey) {
		lastRoomPrefetchKey = selectedRoom;
		void prefetchScriptsForRoom(selectedRoom, roomDeviceKeys);
	} else if (!selectedRoom) {
		lastRoomPrefetchKey = '';
		roomScriptsPrefetchSeq += 1;
		fetchedScriptsAvailability = {};
		isLoadingRoomScripts = false;
	}

	const populateScriptListForDevice = async (device: Device, deviceKey: string) => {
		const deviceRealId = getDeviceRealId(device);
		const scripts = await loadDeviceScripts(device, $devices_settings);
		const settings = ($devices_settings[deviceRealId] || {}) as Record<string, unknown>;

		sceneData.scriptAction = {
			...sceneData.scriptAction,
			scriptListDeviceKey: deviceKey,
			scriptListRealId: deviceRealId,
			scriptListOptions: mapScriptsToSelectOptions(scripts, settings),
		};
	};

	const validateDeviceStep = async (deviceKey: string) => {
		const device = $devices[deviceKey] as Device | undefined;
		if (!device) {
			sceneData.isValid = false;
			sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.RUN_SCRIPT_DEVICE_STEP;
			return;
		}

		const deviceRealId = getDeviceRealId(device);
		const channel = getDeviceChannelFromKey(deviceKey);
		sceneData.selectedDevice = {
			value: deviceKey,
			title: device.name || deviceKey,
			id: deviceKey,
		};
		sceneData.scriptAction = {
			...sceneData.scriptAction,
			device: {
				id: deviceRealId,
				channel: Number.isFinite(channel) ? channel : 0,
			},
			roomId: selectedRoom || String(device.room_id ?? ''),
			scriptId: undefined,
			scriptName: undefined,
			scriptListDeviceKey: deviceKey,
			scriptListRealId: deviceRealId,
			scriptListOptions: [],
		};
		sceneData.isValid = true;
		sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.RUN_SCRIPT_DEVICE_STEP;
		skipSelectionClearForEdit = false;

		await populateScriptListForDevice(device, deviceKey);
	};

	const handleRoomChange = (detail: unknown) => {
		const roomId = selectValue(detail);
		lastRoomPrefetchKey = '';
		fetchedScriptsAvailability = {};
		skipSelectionClearForEdit = false;
		sceneData.scriptAction = {
			...sceneData.scriptAction,
			roomId,
			device: { id: undefined, channel: 0 },
			scriptId: undefined,
			scriptName: undefined,
			scriptListDeviceKey: '',
			scriptListRealId: '',
			scriptListOptions: [],
		};
		sceneData.selectedDevice = {};
		sceneData.isValid = false;
		sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.RUN_SCRIPT_DEVICE_STEP;
	};

	const onSelectDevice = (device) => {
		if (device.disabled) {
			return;
		}
		const deviceKey = device.value || device.id;
		const selectedDevice = $devices[deviceKey] as Device | undefined;
		if (
			!selectedDevice ||
			!deviceHasScriptsFromAvailability(deviceScriptsAvailability, selectedDevice)
		) {
			return;
		}
		void validateDeviceStep(deviceKey);
	};
</script>

<Stack gap="m">
	<Text element="h2" class="mb-0">
		{$format('wizard_step_device', { default: 'Device' })}
	</Text>

	<Select
		id="scene-wizard-run-script-room"
		label={$format('select_room_step', { default: 'Select room' })}
		options={roomOptions}
		value={selectedRoomOption}
		icon="fas fa-door-open"
		on:change={(e) => handleRoomChange(e.detail)}
	/>

	{#if selectedRoom}
		{#if isLoadingRoomScripts}
			<Text variant="body" size="s">{$format('loading', { default: 'Loading...' })}</Text>
		{/if}

		<SelectList
			bind:firstSelectedRef
			title={$format('select_device', { default: 'Select device' })}
			list={availableDevices}
			variant="radio"
			withSearch={availableDevices.length > 5}
			on:click={(e) => onSelectDevice(e.detail)}
		/>
	{/if}
</Stack>
